import { FaSolidFloppyDisk, FaSolidTrash } from "solid-icons/fa"
import { For, Show, createEffect, createSignal, onCleanup } from "solid-js"
import imageLikeToBlob from "../../lib/image/imageLikeToBlob"
import { getDebugImageList, setDebugImageList } from "../../rx/signal/useSharedDebugImageListSignal"
import EmSmallTextMuted from "../Util/EmSmallTextMuted"

interface DebugImageItem {
  url: string;
  name?: string;
  element?: HTMLImageElement | HTMLCanvasElement;
}

function downloadImage(url: string, name: string = 'debug', element?: HTMLElement) {
  // Try to get name from element's alt or title attribute if not provided
  if (!name && element) {
    name = (element as HTMLImageElement).alt || 
           element.getAttribute('title') || 
           element.getAttribute('data-name') || 
           'debug';
  }
  const timestamp = Date.now();
  const img = new Image();
  img.crossOrigin = 'anonymous';
  
  // Helper to get a safe filename with extension
  const getSafeFilename = (width: number, height: number, mimeType: string) => {
    const ext = mimeType.split('/').pop() || 'png';
    return `${timestamp}_${name}_${width}x${height}.${ext}`;
  };

  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      ctx.drawImage(img, 0, 0);
      
      // Always use PNG for consistency and quality
      const mimeType = 'image/png';
      const filename = getSafeFilename(img.width, img.height, mimeType);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const a = document.createElement('a');
          a.download = filename;
          a.href = URL.createObjectURL(blob);
          a.click();
          URL.revokeObjectURL(a.href);
        }
      }, mimeType);
    }
  };
  
  img.onerror = () => {
    console.error('Failed to load image for download');
  };
  
  img.src = url;
}

export default function DebugImageOverlay() {
  const [images, setImages] = createSignal<DebugImageItem[]>([]);

  // Keep track of generated URLs so we can revoke them on changes/unmount
  let currentUrls: string[] = [];
  function revokeAll() {
    for (const u of currentUrls) URL.revokeObjectURL(u);
    currentUrls = [];
  }

  createEffect(() => {
    const list = getDebugImageList();
    // console.log('list', list)
    if (!list || list.length === 0) {
      setImages([]);
      return;
    }

    // Rebuild URLs when the list changes
    ;(async () => {
      try {
        // Cleanup old
        revokeAll();

        const nextImages: DebugImageItem[] = [];
        for (const [index, item] of list.entries()) {
          try {
            const blob = await imageLikeToBlob(item);
            if (blob) {
              const url = URL.createObjectURL(blob);
              // Get the name from the item if it's an object with a name property
              const name = typeof item === 'object' && item !== null && 'name' in item 
                ? String(item.name) 
                : undefined;
              nextImages.push({ url, name });
            }
          } catch (error) {
            console.error(`Error processing debug image ${index}:`, error);
          }
        }
        
        currentUrls = nextImages.map(img => img.url);
        setImages(nextImages);
      } catch (error) {
        console.error('Error in debug image processing:', error);
        setImages([]);
      }
    })();
  });

  onCleanup(() => {
    revokeAll();
  });

  return (
    <Show when={images().length > 0}>
      <div
        id="debugImageOverlay"
        style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          'z-index': 2147483647,
          'pointer-events': 'none'  // Make overlay non-interactive
        }}
      >
        {/* Debug panel container */}
        <div 
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            'z-index': 1,
            padding: '8px',
            'pointer-events': 'auto',  // Only the panel is interactive
            'max-height': '100vh',
            'overflow-y': 'auto',
            'overscroll-behavior': 'contain',
            'scrollbar-width': 'thin',
            'scrollbar-color': 'rgba(255,255,255,0.3) transparent',
            'background': 'rgba(0,0,0,0.7)',
            'backdrop-filter': 'blur(4px)',
            'border-radius': '0 0 8px 0',
            'box-shadow': '2px 2px 10px rgba(0,0,0,0.5)'
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setDebugImageList([])}
            style={{
              'position': 'absolute',
              'top': '8px',
              'right': '8px',
              'background': 'rgba(255, 0, 0, 0.7)',
              'border': 'none',
              'color': 'white',
              'border-radius': '50%',
              'width': '24px',
              'height': '24px',
              'display': 'flex',
              'align-items': 'center',
              'justify-content': 'center',
              'cursor': 'pointer',
              'font-size': '14px',
              'font-weight': 'bold',
              'z-index': '2'
            }}
            onMouseOver={(e) => {
              const target = e.currentTarget as HTMLElement;
              target.style.background = 'rgba(255, 0, 0, 0.9)';
            }}
            onMouseOut={(e) => {
              const target = e.currentTarget as HTMLElement;
              target.style.background = 'rgba(255, 0, 0, 0.7)';
            }}
            title="Close all debug images"
          >
            ×
          </button>
          <For each={images()}>
            {(img, i) => (
              <div style={{
                display: 'flex',
                'flex-direction': 'column',
                'align-items': 'flex-start',
                gap: '4px',
                background: 'rgba(0,0,0,0.4)',
                'backdrop-filter': 'blur(2px)',
                padding: '8px',
                'border-radius': '4px',
                'max-width': 'calc(40vw + 100px)',
                'flex-shrink': '0'
              }}>
                <div style={{
                  color: '#fff',
                  'font-size': '12px',
                  'font-family': 'monospace',
                  padding: '2px 4px',
                  background: 'rgba(0,0,0,0.5)',
                  'border-radius': '3px',
                  'align-self': 'stretch',
                  'text-overflow': 'ellipsis',
                  overflow: 'hidden',
                  'white-space': 'nowrap'
                }}>
                  {img.name || EmSmallTextMuted('Untitled')}
                </div>
                <div style={{ display: 'flex', gap: '8px', 'align-items': 'flex-start' }}>
                  <img
                    src={img.url}
                    alt={img.name || `Debug image ${i()}`}
                    title={img.name}
                    style={{
                      width: "auto",
                      height: "auto",
                      "max-width": "40vw",
                      "max-height": "40vh",
                      border: "1px solid rgba(255,255,255,0.3)",
                      "pointer-events": "auto"
                    }}
                  />
                  <div style={{ display: 'flex', 'flex-direction': 'column', gap: '4px' }}>
                    <button
                      title="Save Image"
                      onClick={(e: MouseEvent) => {
                        const target = e.currentTarget as HTMLElement;
                        const imgElement = target.closest('div')?.querySelector('img');
                        downloadImage(img.url, img.name || `debug_${i()}`, imgElement || undefined);
                      }}
                      style={{
                        background: 'rgba(50, 50, 50, 0.8)',
                        border: '1px solid #666',
                        color: '#fff',
                        padding: '4px 8px',
                        'border-radius': '4px',
                        cursor: 'pointer',
                        'font-size': '12px',
                        'white-space': 'nowrap',
                        'pointer-events': 'auto',
                        'flex-shrink': '0',
                        'align-self': 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e: MouseEvent) => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(70, 70, 70, 0.9)';
                      }}
                      onMouseOut={(e: MouseEvent) => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(50, 50, 50, 0.8)';
                      }}
                    >
                      <FaSolidFloppyDisk />
                    </button>
                    <button
                      title="Remove Image"
                      onClick={() => {
                        const currentList = getDebugImageList();
                        setDebugImageList(currentList.filter((_, index) => index !== i()));
                      }}
                      style={{
                        background: 'rgba(80, 20, 20, 0.8)',
                        border: '1px solid #ff4444',
                        color: '#fff',
                        padding: '4px 8px',
                        'border-radius': '4px',
                        cursor: 'pointer',
                        'font-size': '12px',
                        'white-space': 'nowrap',
                        'pointer-events': 'auto',
                        'flex-shrink': '0',
                        'align-self': 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e: MouseEvent) => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(120, 30, 30, 0.9)';
                      }}
                      onMouseOut={(e: MouseEvent) => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(80, 20, 20, 0.8)';
                      }}
                    >
                      <FaSolidTrash />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
}
